import { detect } from "detect-browser";
import { debounce } from "lodash";
import { DefaultFilterOptions } from "./constants/variables";
import { API_URL, NO_IMAGE_AVAILABLE } from "./environment";
import { useTranslation } from "react-i18next";

// * Global
export * from "./customHook";

const characterList = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

export const toAbsoluteUrl = (pathname) => process.env.PUBLIC_URL + pathname;

export function getDeviceInfo() {
  const browser = detect();
  const deviceInfo = { deviceName: "", deviceId: "" };
  deviceInfo.deviceName = `${browser.os},${browser.name},v${browser.version}`;
  deviceInfo.deviceId = makeid(36);
  return deviceInfo;
}

function makeid(length) {
  let result = "";
  const charactersLength = characterList.length;
  for (let i = 0; i < length; i++) {
    result += characterList.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export function getCurrentUrl(location) {
  return location.pathname.split(/[?#]/)[0];
}
export function checkIsActive(location, url) {
  const current = getCurrentUrl(location);
  const currentSplit = current.split("/");

  if (!current || !url) {
    return false;
  }

  if (current === url) {
    return true;
  }

  if (url.split("/").every((urlPiece) => currentSplit.includes(urlPiece))) {
    return true;
  }

  return false;
}

// * this function for download file from server
export const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Network response was not ok");

    const blob = await response.blob();
    const urlBlob = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = urlBlob;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(urlBlob);
  } catch (error) {
    console.error("There was an error downloading the file:", error);
  }
};

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const toastGenerator = ({ payrollConfirmAlert = false, status = "success", message = "" }) => {
  const lang = localStorage.getItem("i18nextLng");
  const toastTitle = {
    success: () => (lang === "kh" ? `ព័ត៌មាន` : lang === "en" ? `Information` : lang === "cn" ? `信息` : ``),
    warning: () => (lang === "kh" ? `ព័ត៌មាន` : lang === "en" ? `Information` : lang === "cn" ? `信息` : ``),
    error: () => (lang === "kh" ? `កំហុស` : lang === "en" ? `Something went wrong` : lang === "cn" ? `错误` : ``),
    info: () => (lang === "kh" ? `ព័ត៌មាន` : lang === "en" ? `Information` : lang === "cn" ? `信息` : ``),
  };
  const toastDescription = {
    success: () => (message ? message : lang === "kh" ? `ទិន្ន័យបានរក្សាទុក` : lang === "en" ? `Data has been saved.` : lang === "cn" ? `数据已保存` : ``),
    warning: () => (message ? message : lang === "kh" ? `មិនអាចរក្សាទុកទិន្ន័យ` : lang === "en" ? `Unable to save data.` : lang === "cn" ? `无法保存数据` : ``),
    error: () => (message ? message : lang === "kh" ? `សូមព្យយាមម្តងទៀត` : lang === "en" ? `Please try again.` : lang === "cn" ? `请重试` : ``),
    info: () => (message ? message : lang === "kh" ? `ព័ត៌មាន` : lang === "en" ? `Information` : lang === "cn" ? `信息` : ``),
  };

  return {
    title: toastTitle[status](),
    description: toastDescription[status](),
    fontSize: "sm",
    status: status,
    variant: "subtle",
    duration: payrollConfirmAlert ? 80000 : 3000,
    position: "top",
    isClosable: true,
  };
};

export const isValidDate = (value) => {
  return value && value instanceof Date && !isNaN(value) ? value : null;
};

//short sentence ...
export function shortenSentence(sentence, maxLength = 30) {
  if (sentence?.length > maxLength) {
    return sentence.slice(0, maxLength) + "...";
  }
  return sentence;
}

// Available format: "integer", "decimal", "percentage", "currency"
export function formatNumber(input, formatType = "integer", options = {}) {
  if (input == null || input === "" || isNaN(input)) return "";
  const num = parseFloat(input);

  switch (formatType) {
    case "integer": {
      // "integer": "0,000"
      const parts = Math.round(num).toString().split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return parts.join("");
    }
    case "decimal": {
      // "decimal": "0,000.00"
      const parsedNum = num.toFixed(options || 2);
      const parts = parsedNum.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return parts.join(".");
    }
    case "percentage": {
      // "percentage": "0.00%"
      return `${(num * 100).toFixed(2)}%`;
    }
    case "currency": {
      // "currency": "$0,000.00"
      // or formatNumber(1234567.89, "currency", {
      //   symbol: "€", decimalPlaces: 2
      // })); -> Output: "€1,234,567.89"
      const { symbol = "៛", decimalPlaces = 2 } = options;
      const parsedNum = num.toFixed(decimalPlaces);
      const parts = parsedNum.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return `${symbol}${parts.join(".")}`;
    }
    case "scientific": {
      // "scientific": "0.00e+00"
      return num.toExponential(2);
    }
    case "compact": {
      // "compact": 1234567.89 -> 1.23M
      return new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 2,
      }).format(num);
    }
    case "custom": {
      // "custom": formatNumber(1234567.89, "custom", {
      //   customFormatter: (num) => `Custom: ${num.toFixed(1)}`
      // }));   -> Output: "Custom: 1234567.9"
      const { customFormatter } = options;
      if (typeof customFormatter === "function") return customFormatter(num);
      return "";
    }
    case "hour":
      // "hour": "1h 30m"
      // or formatNumber(120, "hour") -> Output: "2h"
      return `${Math.floor(num / 60)}h ${num % 60}m`;
    case "minute_to_hour":
      // "minute_to_hour": "1h 30m"
      // or formatNumber(120, "minute_to_hour") -> Output: "2h"
      const hours = Math.floor(num / 60);
      const minutes = num % 60;
      return hours > 0 ? `${hours}h ${minutes > 0 ? `${minutes}m` : ""}` : `${minutes}m`;
    default:
      return "";
  }
}

// Available format: "YYYY-MM-DD", "DD-MMM", "DD-MMM-YYYY","DD-MMM-YYYY HH:mm", "YYYY-MM-DD HH:mm", "DD/MM/YYYY HH:mm", "DD-MMM-YYYY HH:mm a"
export function formatDate(dateInput, formatString = "YYYY-MM-DD") {
  if (!dateInput || (typeof dateInput !== "string" && !(dateInput instanceof Date))) return undefined;
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(date.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");
  const day = pad(date.getDate());
  const monthShort = date.toLocaleString("en-US", { month: "short" });
  const monthNumeric = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  switch (formatString) {
    case "YYYY-MM-DD":
      return `${year}-${monthNumeric}-${day}`;
    case "DD-MMM":
      return `${day}-${monthShort}`;
    case "DD-MMM-YYYY":
      return `${day}-${monthShort}-${year}`;
    case "DD-MMM-YYYY HH:mm":
      return `${day}-${monthShort}-${year} ${hours}:${minutes}`;
    case "DD-MMM-YYYY HH:mm a":
      const amPm = hours >= 12 ? "PM" : "AM";
      return `${day}-${monthShort}-${year} ${pad(((hours + 11) % 12) + 1)}:${minutes} ${amPm}`;
    case "DD/MM/YYYY HH:mm":
      return `${day}/${monthNumeric}/${year} ${hours}:${minutes}`;
    case "YYYY-MM-DD HH:mm":
      return `${year}-${monthNumeric}-${day} ${hours}:${minutes}`;
    case "HH:mm":
      return `${hours}:${minutes}`;
    default:
      return "";
  }
}

export const startOf = (type, date = new Date()) => {
  let result;
  switch (type) {
    case "day":
      result = new Date();
      break;
    case "week":
      let day = date.getDay();
      result = new Date(date.getFullYear(), date.getMonth(), date.getDate() + (day === 0 ? -6 : 1) - day);
      break;
    case "month":
      result = new Date(date.getFullYear(), date.getMonth(), 1);
      break;
    case "year":
      result = new Date(date.getFullYear(), 0, 1);
      break;
    default:
      result = date;
  }
  return result;
};

export const endOf = (type, date = new Date()) => {
  let result;
  switch (type) {
    case "day":
      result = new Date();
      break;
    case "week":
      let day = date.getDay();
      result = new Date(date.getFullYear(), date.getMonth(), date.getDate() + (day === 0 ? 0 : 7) - day);
      break;
    case "month":
      result = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      break;
    case "year":
      result = new Date(date.getFullYear(), 11, 31);
      break;
    default:
      result = date;
  }
  return result;
};

// Output: 012 345 6789, 012 345 6789, 012 345 6789
export function formatPhoneNumber(input) {
  if (!input) return "";
  return input
    .split(",")
    .map((num) => {
      const digits = num.trim().replace(/\D/g, "");
      return digits.length >= 6 ? digits.replace(/(\d{3})(\d{3})(\d{4})/, "$1 $2 $3").trim() : num.trim();
    })
    .filter(Boolean)
    .join(", ");
}

// Combine array of objects
export function CombineArrayObject(data = [], def = {}) {
  const defaultOption = DefaultFilterOptions();
  return [defaultOption, ...Object.values(data)];
}

export const backToParentList = debounce((history, parentUrl) => {
  history.push(`${parentUrl}/list`);
}, 1000);

/**
 * Generates a thumbnail from a video URL
 * @param {string} videoUrl - The URL of the video
 * @returns {Promise<string>} - A promise that resolves to the thumbnail data URL
 */
export const generateVideoThumbnail = (videoUrl) => {
  return new Promise((resolve, reject) => {
    if (!videoUrl) {
      resolve(NO_IMAGE_AVAILABLE);
      return;
    }

    const video = document.createElement("video");
    video.src = /^https?:\/\//i.test(videoUrl) ? videoUrl : API_URL + videoUrl;
    video.crossOrigin = "anonymous";
    video.muted = true;

    const captureThumbnail = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg");
        resolve(dataUrl);
      } catch (err) {
        console.error("Error generating thumbnail", err);
        resolve(NO_IMAGE_AVAILABLE);
      }
    };

    video.onloadeddata = () => {
      const seekTime = Math.min(1, video.duration * 0.25);
      if (video.readyState >= 2) {
        video.currentTime = seekTime;
      } else {
        video.addEventListener(
          "canplay",
          () => {
            video.currentTime = seekTime;
          },
          { once: true }
        );
      }
    };

    video.onseeked = captureThumbnail;
    video.onerror = () => {
      console.error("Error loading video");
      resolve(NO_IMAGE_AVAILABLE);
    };
  });
};

// * Remove file extension
export const removeFileExtension = (filename) => {
  return filename.replace(/\.[^/.]+$/, "");
};

// * Helper function for sum total of specific field
export const findTotalSpecificField = (data = [], fields = []) => {
  return (
    data?.reduce((acc, item) => {
      fields.forEach((field) => (acc[field] += parseFloat(item[field]) || 0));
      return acc;
    }, Object.fromEntries(fields.map((f) => [f, 0]))) || Object.fromEntries(fields.map((f) => [f, 0]))
  );
};

// * Translate Language
export const Translate = (en, kh, cn) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  switch (currentLanguage) {
    case "en":
      return en;
    case "kh":
      return kh;
    case "cn":
      return cn;
    default:
      return en;
  }
};

/**
 * Converts a formatted currency string to a number
 * Removes commas, negative signs, and dollar symbols
 * @param {string|number} amount - The amount to convert
 * @returns {number} The cleaned number value
 * @example
 * convertToNumber("$1,234.56") // returns 1234.56
 * convertToNumber("1,234.56") // returns 1234.56
 */
export const convertToNumber = (amount) => {
  if (!amount) return 0;
  return Number(String(amount).replace(/[,$]/g, "")) || 0;
};

/**
 * Formats a number as currency with optional prefix and configurable decimal places
 * @param {string} input - The input value to format
 * @param {string} prefix - Optional prefix to add before the number
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(input, prefix = "", decimals = 2) {
  if (!input) return "";

  // Remove non-digits/dots and format with commas
  const formatNumber = (num) => num.replace(/\D/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if (input.includes(".")) {
    const [leftSide, rightSide] = input.split(".");
    // Limit decimal places based on decimals prop
    return `${prefix}${formatNumber(leftSide)}.${rightSide.slice(0, decimals)}`;
  }

  return `${prefix}${formatNumber(input)}`;
}
/**
 * Formats currency value on input blur event
 * @param {React.FocusEvent<HTMLInputElement>} event - Input blur event
 * @param {string} field - Form field name to update
 * @param {object} methods - React Hook Form methods object
 * @param {number} decimals - Number of decimal places to format to
 */
export function onBlurCurrency(event, field, methods, decimals = 2) {
  const value = event.target.value;
  if (!value) return;

  let formatted = value;
  const [whole, decimal = ""] = value.split(".");

  // Add zeros to match desired decimal places
  if (decimal.length === 0) {
    formatted = `${whole}.${"0".repeat(decimals)}`;
  } else if (decimal.length < decimals) {
    formatted = `${whole}.${decimal}${"0".repeat(decimals - decimal.length)}`;
  }

  methods.setValue(field, formatted);
}

/**
 * Validates keypress events for currency inputf
 * Only allows numbers, one decimal point, and commas
 * @param {Event} e - Keypress event
 */
export function onKeyPressCurrency(e) {
  const isValidKey = /[0-9.,]/.test(e.key);
  const hasDecimal = e.target.value.includes(".");
  const isDecimalAttempt = /[.,]/.test(e.key);

  if (!isValidKey || (isDecimalAttempt && hasDecimal)) {
    e.preventDefault();
  }
}

/**
 * Handles currency input changes and formats value while typing
 * @param {React.ChangeEvent<HTMLInputElement>} event - Input change event
 * @param {string} field - Form field name to update
 * @param {object} methods - React Hook Form methods object
 * @param {number} decimals - Number of decimal places allowed
 */
export function onChangeCurrency(event, field, methods, decimals = 2) {
  const input = event.target;
  const cursorStart = input.selectionStart;
  let formatted = input.value;
  let cursorOffset = 0;

  // Handle leading decimal and adjust cursor offset
  if (formatted.startsWith(".")) {
    formatted = `0${formatted}`;
    cursorOffset = 1;
  }

  // Remove leading zeros unless followed by decimal
  if (formatted.length > 1 && formatted.startsWith("0") && formatted[1] !== ".") {
    formatted = formatted.substring(1);
  }

  const formattedValue = formatCurrency(formatted, "", decimals);
  methods.setValue(field, formattedValue);

  // Calculate new cursor position
  setTimeout(() => {
    if (cursorStart === formatted.length) {
      input.setSelectionRange(formattedValue.length, formattedValue.length);
    } else {
      const beforeCommas = (formatted.slice(0, cursorStart).match(/,/g) || []).length;
      const afterCommas = (formattedValue.slice(0, cursorStart).match(/,/g) || []).length;
      const offset = afterCommas - beforeCommas;
      const newPosition = cursorStart + offset + cursorOffset;
      input.setSelectionRange(newPosition, newPosition);
    }
  }, 0);
}
